/**
 * scenery/hills.ts — the hill/dune placement rule (M98) + the authored-lake
 * basin pass (nyc-lake round, bottom of the file).
 *
 * Hills are placed by CENTRE but reach `r` sideways, so a big dome rolled
 * close to the road used to BULGE over the rail as a bare green wall that hid
 * the ball (measured: up to 25.6 units past the rail in Rio). Every zone now
 * routes its hill lateral through `hillLat`, which pushes the centre outward
 * until the dome's EDGE keeps a clear margin off the road. The rng draws are
 * untouched (same values, clamped afterwards) so every other placement in
 * every city is byte-identical — enforced by the cross-city corridor test.
 *
 * The "environment bleeding onto the track" feel the bulges used to (badly)
 * provide now comes from the leaning overhang trees (scenery/leanTrees.ts).
 *
 * The tree round adds the UN-BURIAL PASS at the bottom of the file: trees are
 * planted at road grade while these domes rise over them, so anything inside a
 * dome's footprint is swallowed. See the block comment there.
 */
import { RIO_JUNGLE } from './palettes';

/** Gap between a hill's edge and the guardrail at PLACEMENT time. The zone
 *  builders clamp in their local step frame; on curves the true arc-projected
 *  lateral can come out a couple of units tighter, so this carries cushion —
 *  the corridor test asserts the true projected edge stays ≥ 4 clear. */
export const HILL_EDGE_MARGIN = 7;

/** The guaranteed (arc-projected) clearance the corridor test enforces. */
export const HILL_EDGE_GUARANTEE = 4;

/** Clamp a hill's lateral so its edge stays off the road. */
export function hillLat(hw: number, want: number, r: number): number {
  return Math.max(want, hw + HILL_EDGE_MARGIN + r);
}

// ---- THE UN-BURIAL PASS (tree round) --------------------------------------
//
// THE DEFECT the owner reported: "ensure the trees aren't buried [in
// hillsides]". M118 measured Rio 251, NYC 258, Patagonia 204, Dakar 41; this
// round re-measured with "trunk inside a dome footprint, sunk more than half
// the tree's height" and got Rio 244, Patagonia 204, Dakar 40 — but NYC ZERO
// on both seeds (its park domes sit at |lateral| 50+ with their inner edge
// outboard of the tree band). NYC's dome problem is its BUILDINGS, which
// M118 also flagged and which this pass deliberately does not touch.
//
// ROOT CAUSE: there is no terrain heightfield. ZoneCtx.at() plants EVERYTHING
// at the ROAD centreline elevation (place.ts), while a hill is a separate
// half-buried dome mesh whose centre also sits at road grade and which then
// rises `h` above it. Nothing reconciles the two, so any tree inside a dome's
// footprint is swallowed to whatever depth the dome has there. finishClear
// already deletes the worst of them inside the finish window and its comment
// says the rest "needs the trees LIFTED, not deleted" — this is that lift.
//
// IT IS Y-ONLY AND RNG-FREE, which is what makes it byte-safe under this
// repo's determinism rules: no draw is added, removed or reordered, and no
// x/z moves, so every pinned instance count and layout survives untouched.
//
// WHY IT IS NOT THE BARE ANALYTIC SURFACE. The dome MESHES are coarse
// (SphereGeometry(1, 10, 7) for the plain family) and the jungle/terrain
// families displace their vertices radially by up to ±6% — so the analytic
// half-ellipsoid runs ABOVE the triangles that actually render, and planting
// on it would trade buried trees for LEVITATING ones.
//
// MEASURED: a flat "sample the ellipsoid a few percent further out" fudge does
// NOT work. The error is nothing at the crest and unbounded at the rim (where
// the slope goes vertical), so one number is either optimistic in the middle or
// deletes the lift entirely round the edge — it left Patagonia trees 5.8 units
// under their own snow dome. `domeMeshY` below therefore reconstructs the
// FACETED surface: the azimuthal facet pulls the ring polygon in to its
// inradius (worst case, so the phase of SphereGeometry's first vertex can never
// matter), and the polar band is a straight chord between two ring latitudes,
// exactly as the triangles are. It is evaluated on the COARSEST dome family, so
// the denser jungle/terrain domes simply come out bedded a little deeper.

/** The plain dome mesh (builders' HILL_GEO). The jungle (20×13) and terrain
 *  (24×14) families are denser, so modelling everything on this one is
 *  conservative for them by construction. */
const DOME_SEG_W = 10;
const DOME_SEG_H = 7;
/** Worst-azimuth pull-in: a ring polygon's inradius over its circumradius. */
const DOME_INRADIUS = Math.cos(Math.PI / DOME_SEG_W);

/** Height of the FACETED dome mesh above its own grade, at horizontal distance
 *  `dd` from the centre, at the worst azimuth. Pure; the un-burial pass's
 *  entire claim that it can never float a tree rests on this being ≤ what
 *  renders. Returns 0 outside the footprint. */
export function domeMeshY(dd: number, r: number, h: number): number {
  if (r <= 0 || h <= 0 || dd < 0) return 0;
  // The point sits on the ring polygon of circumradius `rho`, not `dd`.
  const rho = dd / DOME_INRADIUS;
  if (rho >= r) return 0;
  // Which polar band of the sphere's latitude rings the point falls in.
  const phi = Math.asin(Math.min(1, rho / r));
  const band = Math.PI / DOME_SEG_H;
  const k = Math.floor(phi / band);
  const p0 = k * band;
  const p1 = Math.min(Math.PI / 2, (k + 1) * band);
  const r0 = Math.sin(p0);
  const r1 = Math.sin(p1);
  // Straight chord between the two ring latitudes — what the triangles are.
  const t = r1 - r0 > 1e-9 ? Math.min(1, Math.max(0, (rho / r - r0) / (r1 - r0))) : 1;
  return h * (Math.cos(p0) + t * (Math.cos(p1) - Math.cos(p0)));
}

export const HILL_LIFT = {
  /** Fraction of the modelled mesh height kept — the jungle/terrain families'
   *  radial vertex displacement (up to −6%) on top of the facet model. */
  keep: 0.93,
  /** World units the trunk foot sinks into the slope, so it reads as planted. */
  bed: 0.5,
  /** …deeper on CANOPY-ART domes (Rio's Tijuca, whose hills are painted with
   *  the canopy-clump texture and ARE the jungle mass). Trees there should rise
   *  OUT of the canopy like emergents rather than perch on a green ball —
   *  which is also what rio-ts QA asked for when it called the fully buried
   *  ones "scribble streaks" against the dome faces. */
  canopyBed: 1.6,
  /** Below this the burial simply isn't visible; leave the tree where it is
   *  (and keep the pass from nudging thousands of instances for nothing). */
  minLift: 0.4,
} as const;

/** Hill palettes whose dome is painted with the canopy-clump texture. KEEP IN
 *  STEP with builders' JUNGLE_HILL_SET (same RIO_JUNGLE source; the disjoint
 *  set is already test-pinned in tests/world/rioScenery.test.ts). */
export const CANOPY_HILL_COLORS: readonly number[] = RIO_JUNGLE;
const CANOPY_HILLS = new Set<number>(CANOPY_HILL_COLORS);

/** Anything the pass can stand on a hill: it only ever reads x/z and writes y. */
export interface LiftableInst {
  x: number;
  y: number;
  z: number;
}

/** The world Y a trunk foot should sit at on this dome field, or −Infinity if
 *  no dome reaches the point. Pure; exported for the regression tests. */
export function hillSurfaceY(
  x: number,
  z: number,
  hills: readonly { x: number; y: number; z: number; r: number; h: number; color?: number }[],
): number {
  let best = -Infinity;
  for (const h of hills) {
    const rise = domeMeshY(Math.hypot(x - h.x, z - h.z), h.r, h.h) * HILL_LIFT.keep;
    if (rise <= 0) continue;
    const canopy = h.color !== undefined && CANOPY_HILLS.has(h.color);
    // Bed the foot in — but never by more than the slope actually offers, or a
    // tree on a shallow flank would be pushed BELOW where it already stood.
    const bed = Math.min(rise, canopy ? HILL_LIFT.canopyBed : HILL_LIFT.bed);
    const y = h.y + rise - bed;
    if (y > best) best = y;
  }
  return best;
}

/** Stand every instance on top of the dome field instead of inside it. Y-ONLY,
 *  MONOTONE (a tree already clear of the domes is never touched, and nothing is
 *  ever lowered) and rng-free. Returns how many were lifted, for the audit.
 *
 *  MUST run after every pass that moves or DELETES hills — clampHills,
 *  carveLakeBasins and, in Scenery.ts, the finish/shortcut clearings. Lifting
 *  before a dome is deleted would leave the tree standing on nothing. */
export function liftOntoHills(
  items: readonly LiftableInst[],
  hills: readonly { x: number; y: number; z: number; r: number; h: number; color?: number }[],
): number {
  if (!hills.length) return 0;
  let n = 0;
  for (const t of items) {
    const y = hillSurfaceY(t.x, t.z, hills);
    if (y - t.y > HILL_LIFT.minLift) {
      t.y = y;
      n++;
    }
  }
  return n;
}

/** The zone-side clamp works in the local step frame — but a big dome can
 *  ALSO bulge over a DIFFERENT pass of the course (the track loops back;
 *  measured in Rio: a jungle hill 8.7 units over the road two corners
 *  later). This track-aware pass runs after placement: any hill whose edge
 *  truly intrudes is pushed straight out from its nearest road section (a
 *  few iterations, since pushing can change which section is nearest); one
 *  that can't settle is dropped. Deterministic — no rng involved. */
export function clampHills(
  hills: { x: number; y: number; z: number; r: number }[],
  track: {
    halfWidth: number;
    project(x: number, z: number): { distance: number; lateral: number };
    sampleAt(d: number): { x: number; z: number; rightX: number; rightZ: number };
  },
): void {
  const hw = track.halfWidth;
  for (let i = hills.length - 1; i >= 0; i--) {
    const h = hills[i];
    let ok = false;
    for (let iter = 0; iter < 4; iter++) {
      const p = track.project(h.x, h.z);
      if (Math.abs(p.lateral) - h.r >= hw + HILL_EDGE_GUARANTEE) {
        ok = true;
        break;
      }
      const s = track.sampleAt(p.distance);
      const sign = p.lateral >= 0 ? 1 : -1;
      const target = sign * (hw + HILL_EDGE_MARGIN + h.r);
      h.x = s.x + s.rightX * target;
      h.z = s.z + s.rightZ * target;
    }
    if (!ok) {
      const p = track.project(h.x, h.z);
      if (Math.abs(p.lateral) - h.r >= hw + HILL_EDGE_GUARANTEE) ok = true;
    }
    if (!ok) hills.splice(i, 1);
  }
}

// ---- Authored lakes carve their basin (nyc-lake round) ---------------------

/** The disc the WATER pass actually builds from a pond footprint: the organic
 *  outline swings up to 33% proud of r, plus the wet-bank ring. Same number
 *  zones.pondReach and the NYC invariants use — keep them in step. */
export function lakeReach(r: number): number {
  return r * 1.33 + 2.4;
}

export const LAKE_BASIN = {
  /** Breathing room between the dome's foot and the lake's bank. */
  margin: 1.5,
  /** A trim this gentle (fraction of the dome's radius) is invisible, so it is
   *  preferred over moving the dome at all. */
  gentleTrim: 0.72,
  /** Never shrink a dome below this — smaller reads as a bump, not a hill. */
  minR: 12,
} as const;

/** Escape search: how far around the basin rim a displaced dome may slide,
 *  and in what increment. Half a turn each way covers every rim seat. */
const ESCAPE_STEP = Math.PI / 12;
const ESCAPE_ARC = 12;

/** Narrowed track access (satisfied by world/Track, stubable in tests). */
export interface BasinTrackLike {
  halfWidth: number;
  finishDistance: number;
  project(x: number, z: number): { distance: number; lateral: number };
  sampleAt(d: number): { x: number; z: number; rightX: number; rightZ: number };
}

/** Resolve an authored lake (progress + signed lateral) to world coordinates. */
function lakeCentre(
  l: { at: number; lateral: number },
  track: BasinTrackLike,
): { x: number; z: number; rightX: number; rightZ: number } {
  const s = track.sampleAt(l.at * track.finishDistance);
  return {
    x: s.x + s.rightX * l.lateral,
    z: s.z + s.rightZ * l.lateral,
    rightX: s.rightX,
    rightZ: s.rightZ,
  };
}

/** THE BASIN PASS — make room for a city's AUTHORED lakes (City.water.lakes,
 *  hand-placed hero water like NYC's pair flanking Bow Bridge): domes out of
 *  the water, trees out of the water. Delete-free and rng-free, so every draw
 *  and every other placement survives; call it after clampHills. */
export function carveLakeBasins(
  a: {
    hills: { x: number; y: number; z: number; r: number }[];
    trees: { x: number; z: number }[];
    ponds: readonly { x: number; z: number; w: number; d: number }[];
  },
  lakes: readonly { at: number; lateral: number; r: number }[],
  track: BasinTrackLike,
): void {
  if (!lakes.length) return;
  // Water a displaced dome should not land on: the zone-emitted pond
  // footprints (water.planPonds reads them as discs of r = (w+d)/4) and the
  // sibling lakes' own basins.
  const avoid: { x: number; z: number; r: number }[] = a.ponds.map((p) => ({
    x: p.x,
    z: p.z,
    r: (p.w + p.d) / 4,
  }));
  for (const l of lakes) {
    const c = lakeCentre(l, track);
    avoid.push({ x: c.x, z: c.z, r: lakeReach(l.r) + LAKE_BASIN.margin });
  }
  clearHillsForLakes(a.hills, lakes, track, avoid);
  nudgeTreesOffLakes(a.trees, lakes, track);
}

/** Trees the water swallowed, moved to the bank. The zone builder already does
 *  this for the ponds IT places (zones.pushTreesOffPond) but knows nothing
 *  about the derived lakes, so a recovered lake grew trees out of its middle
 *  (measured: 24.7 u from a 30 u lake's centre on the tour seed, 20.2 on the
 *  QA seed). The shift is purely LATERAL and outward, the zone builder's rule
 *  verbatim: the park's ground follows the road's elevation along the course,
 *  so sideways keeps a trunk at exactly the height it was planted at while an
 *  along-course nudge would float or sink it. */
export function nudgeTreesOffLakes(
  trees: { x: number; z: number }[],
  lakes: readonly { at: number; lateral: number; r: number }[],
  track: BasinTrackLike,
): void {
  if (!lakes.length || !trees.length) return;
  for (const l of lakes) {
    const c = lakeCentre(l, track);
    const need = lakeReach(l.r) + LAKE_BASIN.margin;
    const sign = l.lateral >= 0 ? 1 : -1;
    const vx = c.rightX * sign;
    const vz = c.rightZ * sign;
    for (const t of trees) {
      const ux = t.x - c.x;
      const uz = t.z - c.z;
      if (ux * ux + uz * uz >= need * need) continue;
      // |u + push·v| = need, solved for the outward root.
      const uv = ux * vx + uz * vz;
      const push = -uv + Math.sqrt(Math.max(0, uv * uv - (ux * ux + uz * uz) + need * need));
      t.x += vx * push;
      t.z += vz * push;
    }
  }
}

/** Make room for a city's AUTHORED lakes (City.water.lakes — hand-placed hero
 *  water like NYC's pair flanking Bow Bridge).
 *
 *  WHY THIS EXISTS. Hills are placed by centre at `hillLat` = hw + 7 + r, so a
 *  park's dome field always starts ~7 u off the rail and reaches outward —
 *  exactly the band an authored lake occupies. On the left of Bow Bridge SIX
 *  domes stood in the authored basin on both seeds, three of them with their
 *  CENTRE under the water. The water pass could only respond by shoving the
 *  disc away from the domes, which means straight at the road, where it was
 *  rejected and silently deleted: half the authored composition never
 *  rendered, leaving bare ground beside a finished lake (measured).
 *
 *  Scatter yields to authorship: a dome standing in an authored lake is
 *  trimmed if the trim is invisible, otherwise walked out of the basin along
 *  the shortest escape. NEVER deleted (the draw survives, so the instance
 *  count and every other placement are untouched) and never at the cost of the
 *  M98 road guarantee — a dome that would have to bulge over the course stays
 *  put and the water pass's own fit handles the remainder.
 *
 *  Deterministic, geometric, no rng. */
export function clearHillsForLakes(
  hills: { x: number; y: number; z: number; r: number }[],
  lakes: readonly { at: number; lateral: number; r: number }[],
  track: BasinTrackLike,
  /** Other water a displaced dome must try to stay off (discs in world
   *  coordinates — the emitted pond footprints and the sibling lakes). */
  avoid: readonly { x: number; z: number; r: number }[] = [],
): void {
  if (!lakes.length || !hills.length) return;
  const hw = track.halfWidth;
  for (const l of lakes) {
    const s = lakeCentre(l, track);
    const cx = s.x;
    const cz = s.z;
    const need = lakeReach(l.r) + LAKE_BASIN.margin;
    for (const h of hills) {
      const d = Math.hypot(h.x - cx, h.z - cz);
      if (d >= h.r + need) continue; // the dome is already out of the water
      const trimmed = d - need; // radius that stops the dome at the bank
      if (trimmed >= Math.max(LAKE_BASIN.minR, h.r * LAKE_BASIN.gentleTrim)) {
        h.r = trimmed; // an invisible trim beats moving a landform
        continue;
      }
      // Walk it out to the basin rim. The shortest escape is radial, but half
      // the domes here sit INBOARD of the lake, and radial for them means
      // "toward the road" — measured: three of six escapes on the QA seed
      // pointed at the carriageway and had to be refused, leaving the water
      // clipped anyway. So the dome SLIDES AROUND the rim: try the radial
      // bearing first, then alternate outward in ESCAPE_STEP increments until
      // one lands clear of the course. Hills part around a lake instead of
      // being shoved through the road.
      const bearing = d > 1e-3 ? Math.atan2(h.z - cz, h.x - cx) : Math.atan2(s.rightZ, s.rightX);
      const rim = h.r + need;
      // Two-tier seat choice: the nearest seat that clears the course AND
      // lands on nobody else's water, else the nearest that clears the course.
      // MEASURED: without the second condition four displaced domes rolled
      // 45-51 u down-course and parked on the park ponds at 0.577/0.589, whose
      // own dome-push then shoved them onto trees the zone had already moved
      // out of their way — a rescued lake must not cost the pond next door.
      let seat: { x: number; z: number } | null = null;
      let clearSeat: { x: number; z: number } | null = null;
      for (let k = 0; k <= ESCAPE_ARC && !clearSeat; k++) {
        for (const dir of k === 0 ? [1] : [1, -1]) {
          const th = bearing + dir * k * ESCAPE_STEP;
          const nx = cx + Math.cos(th) * rim;
          const nz = cz + Math.sin(th) * rim;
          const p = track.project(nx, nz);
          if (Math.abs(p.lateral) - h.r < hw + HILL_EDGE_GUARANTEE) continue;
          seat ??= { x: nx, z: nz };
          if (avoid.every((w) => Math.hypot(nx - w.x, nz - w.z) >= h.r * 0.8 + w.r * 0.7)) {
            clearSeat = { x: nx, z: nz };
            break;
          }
        }
      }
      const placedAt = clearSeat ?? seat;
      if (placedAt) {
        h.x = placedAt.x;
        h.z = placedAt.z;
      }
      if (!placedAt && trimmed >= LAKE_BASIN.minR) {
        h.r = trimmed; // ringed in by the course — trim as far as it reads
      }
    }
  }
}
